<script>
import InboxLoading from '@/components/inbox/InboxLoading.vue'
import InboxDataTable from '@/components/inbox/InboxDataTable.vue'
import InboxWrapperSearchField from '@/components/inbox/InboxWrapperSearchField.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { EventBus } from '@/helpers/eventBus.js'

export default {
  components: {
    InboxLoading,
    InboxDataTable,
    InboxWrapperSearchField
  },
  data: () => ({
    selectedTab: 1,
    isSearching: false,
    searchResults: [],
    selectedInboxFilters: [],
    selectedClientFilters: [],
    countdownTime: 300,
    sortBy: 'documentId',
    sortDesc: true,
    countdownTimeout: null,
    timerRunning: true,
    searchTerm: ''
  }),
  computed: {
    ...mapGetters([
      'inboxItems',
      'inboxes',
      'clientFilters',
      'inboxFilters',
      'isLoadingInboxes',
      'latestSync',
      'openItems',
      'processingItems',
      'processingItemsOther',
      'closedItems',
      'inboxRetrievalErrors'
    ]),
    ...mapActions(['fetchInbox', 'fetchLatestInboxSync', 'fetchInboxList', 'fetchClientFilters', 'fetchInboxFilters']),
    userSelectedClientFilters: {
      get() {
        return this.selectedClientFilters
      },
      set(newValue) {
        this.selectedClientFilters = newValue
      }
    },
    userSelectedInboxFilters: {
      get() {
        return this.selectedInboxFilters
      },
      set(newValue) {
        this.selectedInboxFilters = newValue
      }
    },
    formattedTime() {
      const minutes = Math.floor(this.countdownTime / 60)
      const seconds = this.countdownTime % 60
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    },
    searchResultsKey() {
      if (!Array.isArray(this.searchResults)) return 'no-results'
      return this.searchResults.map(d => d.id || '').join('-') + '-' + this.isSearching
    }
  },
  watch: {
    selectedTab(newVal) {
      const contextMap = {
        0: 'open',
        1: 'processing',
        2: 'closed'
      }
      const context = contextMap[newVal] || null
      if (context) {
        this.SET_SELECTED_CONTEXT(context)
      } else {
        console.error('No valid context found for selectedTab:', newVal)
      }
    }
  },
  created: async function() {
    this.getSelectedFiltersFromStore()
    this.getSelectedInboxFiltersFromStore()
  },
  mounted() {
    this.startCountdown()
    EventBus.$on('pause-timer', this.pauseTimer)
    EventBus.$on('resume-timer', this.resumeTimer)
  },
  beforeDestroy() {
    if (this.countdownTimeout) {
      clearTimeout(this.countdownTimeout)
    }
    EventBus.$off('pause-timer', this.pauseTimer)
    EventBus.$off('resume-timer', this.resumeTimer)
  },
  methods: {
    ...mapMutations(['SET_SELECTED_CONTEXT']),
    onFoundDocuments(searchResults) {
      this.searchResults = searchResults
      this.selectedTab = 0
      this.isSearching = true
    },
    onClearSearch() {
      this.searchResults = []
      this.selectedTab = 0
      this.isSearching = false
    },
    getSelectedFiltersFromStore() {
      if (localStorage.getItem('selectedClientFilters')) {
        this.userSelectedClientFilters = JSON.parse(localStorage.getItem('selectedClientFilters'))
      }
    },
    getSelectedInboxFiltersFromStore() {
      if (localStorage.getItem('selectedInboxFilters')) {
        this.userSelectedInboxFilters = JSON.parse(localStorage.getItem('selectedInboxFilters'))
      }
    },
    filteredClientItems: function(status) {
      if (status.length > 0) {
        this.userSelectedClientFilters = status
      } else {
        this.userSelectedClientFilters = []
        this.fetchInbox()
      }
    },
    filteredInboxItems: function(items) {
      if (items.length > 0) {
        this.userSelectedInboxFilters = items
      } else {
        this.userSelectedInboxFilters = []
        this.fetchInbox()
      }
    },
    startCountdown() {
      const tick = async () => {
        if (!this.timerRunning) return
        if (this.countdownTime > 0) {
          this.countdownTime--
        } else {
          try {
            await this.$store.dispatch('fetchInbox')
          } catch (error) {
            console.error('Fehler beim Fetch:', error)
          }
          this.countdownTime = 300
        }
        if (this.timerRunning) {
          this.countdownTimeout = setTimeout(tick, 1000)
        }
      }
      tick()
    },
    pauseTimer() {
      if (this.timerRunning) {
        clearTimeout(this.countdownTimeout)
        this.timerRunning = false
      }
    },
    resumeTimer() {
      if (!this.timerRunning) {
        this.timerRunning = true
        this.startCountdown()
      }
    },
    syncAndResetTimer() {
      this.$store.dispatch('fetchInbox')
      this.countdownTime = 300
    },
    onUpdateSort({ sortBy, sortDesc }) {
      this.sortBy = sortBy
      this.sortDesc = sortDesc
    },
    updateSearchTerm(term) {
      this.searchTerm = term
    }
  }
}
</script>
<template>
  <v-container fluid>
    <v-row class="pt-0">
      <v-col cols="2" class="pt-0 pe-0">
        <h4 class="text--black">
          {{ $t('heading.inbox')}}
        </h4>
        <small v-if="inboxItems.hasOwnProperty('length') && inboxItems.length !== 0" class="text--black">
          {{ inboxItems.length }} {{ inboxItems.length > 1 ? $t('global.entries') : $t('global.entry') }}
        </small>
        <small v-else class="text--black">
          {{ $t('global.no.entries') }}
        </small>
      </v-col>
      <v-col cols="5" class="pt-0">
        <v-card>
          <v-tabs v-model="selectedTab" show-arrows>
            <v-tabs-slider />
          <v-tab v-if="isSearching" name="search-results" @click="onClearSearch">
            <v-icon>mdi-chevron-left</v-icon>
            <span class="pl-1">{{ $t('button.back') }}</span>
          </v-tab>
          <v-tab v-if="!isSearching" name="open-inbox">
            <v-icon>mdi-folder-open-outline</v-icon>
            <span class="pl-1">{{ $t('global.open') }}</span>
          </v-tab>
          <v-tab v-if="!isSearching" name="processing-inbox">
            <v-icon>mdi-folder-account-outline</v-icon>
            <span class="pl-1">{{ $t('global.processing') }}</span>
          </v-tab>
          <v-tab v-if="!isSearching" name="closed-inbox">
            <v-icon>mdi-folder-check-outline</v-icon>
            <span class="pl-1">{{ $t('global.closed') }}</span>
          </v-tab>
          <v-tab v-if="inboxRetrievalErrors.length > 0" name="error-inbox">
            {{ $t('global.canceled') }}
          </v-tab>
        </v-tabs>
        </v-card>
      </v-col>
      <v-col class="pt-0">
        <InboxWrapperSearchField
          ref="searchField"
          @search="updateSearchTerm"
          @found-documents="onFoundDocuments"
          @clear-search="onClearSearch"
        />
      </v-col>
      <v-col class="pt-0">
        <!--        REFRESH SYNC-->
        <div id="inbox-refresh-sync-wrapper" style="position:absolute;right:1px">
          <span id="inbox-latest-sync-wrapper" class="float-end me-5 text-right">
            <small class="text-darken-3">
              {{ $t('text.refresh.in') }}{{ latestSync ? formattedTime : '---'}}
              <v-icon
                v-if="latestSync"
                size="small"
                @click="$store.dispatch('fetchInbox')">
                mdi-sync
              </v-icon>
              <br />
              {{ $t('component.inbox.latest.import') }}: {{ latestSync ? latestSync : '---'  }}
            </small>
          </span>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="pt-0">
        <v-card>
          <!--        TAB "SERP"-->
          <v-tabs-items v-model="selectedTab">
          <v-tab-item v-if="isSearching" :key="isSearching">
            <v-divider />
            <InboxDataTable
              v-if="searchResults.length > 0"
              :key="searchResultsKey"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              :items="searchResults"
              :selected-client-filters="userSelectedClientFilters"
              :selected-inbox-filters="userSelectedInboxFilters"
              :search-term="searchTerm"
              @update-sort="onUpdateSort"
              @inbox-updated="$store.dispatch('fetchInbox')"
              @filter-items-by-client="filteredClientItems"
              @filter-items-by-inbox="filteredInboxItems"
              @pause-timer="pauseTimer"
              @resume-timer="resumeTimer"
            />
            <div v-else class="text-center ma-15">
              {{ $t('global.no.result') }}
            </div>
          </v-tab-item>
          <!--        TAB "Offen"-->
          <v-tab-item v-if="!isSearching">
            <InboxLoading />
            <v-divider />
            <InboxDataTable
              v-if="!isLoadingInboxes"
              :key="searchResultsKey"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              :items="openItems"
              :selected-client-filters="userSelectedClientFilters"
              :selected-inbox-filters="userSelectedInboxFilters"
              @update-sort="onUpdateSort"
              @inbox-updated="$store.dispatch('fetchInbox')"
              @filter-items-by-client="filteredClientItems"
              @filter-items-by-inbox="filteredInboxItems"
              @pause-timer="pauseTimer"
              @resume-timer="resumeTimer"
            />
          </v-tab-item>
          <!--        TAB "In Bearbeitung"-->
          <v-tab-item v-if="!isSearching">
            <InboxLoading />
            <v-divider />
            <v-expansion-panels v-if="!isLoadingInboxes" :value="[0, 1]" multiple>
              <v-expansion-panel :title="$t('component.inbox.assigned.to.me')">
                <v-expansion-panel-header>
                  <v-icon>mdi-account</v-icon>
                  <h4>{{ $t('component.inbox.assigned.to.me') }}</h4>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <InboxDataTable
                    v-if="!isLoadingInboxes"
                    :key="searchResultsKey"
                    :sort-by="sortBy"
                    :sort-desc="sortDesc"
                    :items="processingItems"
                    :selected-client-filters="userSelectedClientFilters"
                    :selected-inbox-filters="userSelectedInboxFilters"
                    @update-sort="onUpdateSort"
                    @inbox-updated="$store.dispatch('fetchInbox')"
                    @filter-items-by-client="filteredClientItems"
                    @filter-items-by-inbox="filteredInboxItems"
                    @pause-timer="pauseTimer"
                    @resume-timer="resumeTimer"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel :title="$t('component.inbox.assigned.to.others')">
                <v-expansion-panel-header>
                  <v-icon>mdi-account-multiple</v-icon>
                  <h4>{{ $t('component.inbox.assigned.to.others') }}</h4>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <InboxDataTable
                    v-if="!isLoadingInboxes"
                    :key="searchResultsKey"
                    :sort-by="sortBy"
                    :sort-desc="sortDesc"
                    :items="processingItemsOther"
                    :selected-client-filters="userSelectedClientFilters"
                    :selected-inbox-filters="userSelectedInboxFilters"
                    @update-sort="onUpdateSort"
                    @inbox-updated="$store.dispatch('fetchInbox')"
                    @filter-items-by-client="filteredClientItems"
                    @filter-items-by-inbox="filteredInboxItems"
                    @pause-timer="pauseTimer"
                    @resume-timer="resumeTimer"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-tab-item>
          <!--        TAB "Abgeschlossen"-->
          <v-tab-item v-if="!isSearching">
            <InboxLoading />
            <v-divider />
            <InboxDataTable
              v-if="!isLoadingInboxes"
              :key="searchResultsKey"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              :items="closedItems"
              :selected-client-filters="userSelectedClientFilters"
              :selected-inbox-filters="userSelectedInboxFilters"
              @update-sort="onUpdateSort"
              @inbox-updated="$store.dispatch('fetchInbox')"
              @filter-items-by-client="filteredClientItems"
              @filter-items-by-inbox="filteredInboxItems"
            />
          </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<style>
.v-text-field > .v-input__control > .v-input__slot:before,
.v-text-field > .v-input__control > .v-input__slot:after {
  content: none;
}
</style>
